import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios defaults for longer timeout (AI processing can take time)
axios.defaults.timeout = 60000; // 60 seconds

// Simple response cache for GET requests
const responseCache = new Map();
const CACHE_TTL = 60000; // 60 seconds default cache

// Get cached data with custom TTL
const getCached = (key, ttl = CACHE_TTL) => {
  const entry = responseCache.get(key);
  if (entry && Date.now() - entry.timestamp < ttl) {
    return entry.data;
  }
  responseCache.delete(key);
  return null;
};

const setCached = (key, data, ttl = CACHE_TTL) => {
  responseCache.set(key, { data, timestamp: Date.now(), ttl });
};

const clearCache = (prefix = null) => {
  if (prefix) {
    for (const key of responseCache.keys()) {
      if (key.startsWith(prefix)) responseCache.delete(key);
    }
  } else {
    responseCache.clear();
  }
};

// Auth API
export const authApi = {
  login: (email, password) => axios.post(`${API_URL}/auth/login`, { email, password }),
  register: (email, password, fullName, role) => 
    axios.post(`${API_URL}/auth/register`, { email, password, full_name: fullName, role }),
  forgotPassword: (email) => axios.post(`${API_URL}/auth/forgot-password`, { email }),
  resetPassword: (token, newPassword) => 
    axios.post(`${API_URL}/auth/reset-password`, { token, new_password: newPassword }),
  verifyResetToken: (token) => axios.get(`${API_URL}/auth/verify-reset-token/${token}`),
  getPermissions: () => axios.get(`${API_URL}/auth/permissions`),
  changePassword: (currentPassword, newPassword) => 
    axios.post(`${API_URL}/users/change-password`, { current_password: currentPassword, new_password: newPassword }),
};

// Teams API - cached
export const teamsApi = {
  getAll: () => {
    const cacheKey = 'teams:all';
    const cached = getCached(cacheKey);
    if (cached) return Promise.resolve(cached);
    return axios.get(`${API_URL}/teams`).then(res => {
      setCached(cacheKey, res);
      return res;
    });
  },
  getById: (id) => axios.get(`${API_URL}/teams/${id}`),
  create: (data) => {
    clearCache('teams:');
    return axios.post(`${API_URL}/teams`, data);
  },
  delete: (id) => {
    clearCache('teams:');
    return axios.delete(`${API_URL}/teams/${id}`);
  },
};

// Users API - cached for 60 seconds
export const usersApi = {
  getAll: (params = {}) => {
    const cacheKey = `users:all:${JSON.stringify(params)}`;
    const cached = getCached(cacheKey, 60000);
    if (cached) return Promise.resolve(cached);
    return axios.get(`${API_URL}/users`, { params }).then(res => {
      setCached(cacheKey, res, 60000);
      return res;
    });
  },
  invite: (email, fullName, role, teamId = null) => {
    clearCache('users:');
    return axios.post(`${API_URL}/users/invite`, {
      email,
      full_name: fullName,
      role,
      team_id: teamId
    });
  },
  getPendingInvites: () => axios.get(`${API_URL}/users/pending-invites`),
  updateRole: (userId, role) => axios.put(`${API_URL}/users/${userId}/role?role=${role}`),
  updatePermissions: (userId, preset, customPermissions = null) => 
    axios.put(`${API_URL}/users/${userId}/permissions?preset=${preset}`, customPermissions),
  assignToTeam: (userId, teamId) => axios.put(`${API_URL}/users/${userId}/team?team_id=${teamId}`),
  deactivate: (userId) => axios.put(`${API_URL}/users/${userId}/deactivate`),
  activate: (userId) => axios.put(`${API_URL}/users/${userId}/activate`),
  delete: (userId) => axios.delete(`${API_URL}/users/${userId}`),
};

// Roles (Job Positions) API - cached for 60 seconds
export const rolesApi = {
  getAll: (params = {}) => {
    const cacheKey = `roles:${JSON.stringify(params)}`;
    const cached = getCached(cacheKey);
    if (cached) return Promise.resolve(cached);
    return axios.get(`${API_URL}/roles`, { params }).then(res => {
      setCached(cacheKey, res);
      return res;
    });
  },
  getById: (id) => axios.get(`${API_URL}/roles/${id}`),
  create: (data) => {
    clearCache('roles:');
    return axios.post(`${API_URL}/roles`, data);
  },
  update: (id, data) => {
    clearCache('roles:');
    return axios.put(`${API_URL}/roles/${id}`, data);
  },
  delete: (id) => {
    clearCache('roles:');
    return axios.delete(`${API_URL}/roles/${id}`);
  },
  close: (id) => {
    clearCache('roles:');
    return axios.put(`${API_URL}/roles/${id}/close`);
  },
  parseJD: (data) => axios.post(`${API_URL}/roles/parse-jd`, data),
  generateAI: (id) => axios.post(`${API_URL}/roles/${id}/generate-ai`),
  getAudioBriefing: (id) => axios.get(`${API_URL}/roles/${id}/audio-briefing`),
  uploadAudioBriefing: (id, audioData, audioName) =>
    axios.post(`${API_URL}/roles/${id}/audio-briefing`, { audio_data: audioData, audio_name: audioName }),
  updateAssignments: (id, isOpenToAll, assignedRecruiterIds) => {
    clearCache('roles:');
    return axios.put(`${API_URL}/roles/${id}/assignments`, { 
      is_open_to_all: isOpenToAll, 
      assigned_recruiter_ids: assignedRecruiterIds 
    });
  },
  getRecruiters: () => axios.get(`${API_URL}/recruiters`),
};

// CVs API - cached for quick subsequent loads
export const cvsApi = {
  getAll: (params = {}) => {
    const cacheKey = `cvs:list:${JSON.stringify(params)}`;
    const cached = getCached(cacheKey, 120000);
    if (cached) return Promise.resolve(cached);
    return axios.get(`${API_URL}/cvs`, { params }).then(res => {
      setCached(cacheKey, res, 120000);
      return res;
    });
  },
  getById: (id) => axios.get(`${API_URL}/cvs/${id}`),
  getByRole: (roleId) => {
    const cacheKey = `cvs:role:${roleId}`;
    const cached = getCached(cacheKey, 10000);
    if (cached) return Promise.resolve(cached);
    return axios.get(`${API_URL}/cvs/by-role/${roleId}`).then(res => {
      setCached(cacheKey, res, 10000);
      return res;
    });
  },
  download: (id) => axios.get(`${API_URL}/cvs/${id}/download`),
  create: (data) => {
    clearCache('cvs:');
    return axios.post(`${API_URL}/cvs`, data);
  },
  update: (id, data) => {
    clearCache('cvs:');
    return axios.put(`${API_URL}/cvs/${id}`, data);
  },
  updateStatus: (id, status, notes, joiningDate) => {
    clearCache('cvs:');
    return axios.put(`${API_URL}/cvs/${id}/status`, { status, notes, joining_date: joiningDate });
  },
  updateJoiningDate: (id, joiningDate) => {
    clearCache('cvs:');
    return axios.put(`${API_URL}/cvs/${id}/joining-date`, { joining_date: joiningDate });
  },
  updateOfferedDate: (id, offeredDate) => {
    clearCache('cvs:');
    return axios.put(`${API_URL}/cvs/${id}/offered-date`, { offered_date: offeredDate });
  },
  updateSalaryOffered: (id, salaryLakhs) => {
    clearCache('cvs:');
    return axios.put(`${API_URL}/cvs/${id}/salary-offered`, { salary_offered_lakhs: salaryLakhs });
  },
  parseCV: (data) => axios.post(`${API_URL}/cvs/parse`, data),
  checkDuplicate: (candidateName, phone) =>
    axios.post(`${API_URL}/cvs/check-duplicate`, { candidate_name: candidateName, phone }),
  scoreCV: (id) => axios.post(`${API_URL}/cvs/${id}/score`),
  scoreAllForRole: (roleId) => axios.post(`${API_URL}/cvs/score-all/${roleId}`),
  delete: (id) => {
    clearCache('cvs:');
    return axios.delete(`${API_URL}/cvs/${id}`);
  },
  processOne: (file, roleId) => {
    clearCache('cvs:');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('role_id', roleId);
    return axios.post(`${API_URL}/cvs/bulk-upload`, formData, {
      timeout: 120000,
    });
  },
};

// Feedback API
export const feedbackApi = {
  getAll: (params = {}) => axios.get(`${API_URL}/feedback`, { params }),
  getByCV: (cvId) => axios.get(`${API_URL}/feedback/cv/${cvId}`),
  create: (data) => axios.post(`${API_URL}/feedback`, data),
};

// Time Entries API
export const timeEntriesApi = {
  getAll: (params = {}) => axios.get(`${API_URL}/time-entries`, { params }),
  getToday: () => axios.get(`${API_URL}/time-entries/today`),
  create: (entryType, activityNote = null) => axios.post(`${API_URL}/time-entries`, { 
    entry_type: entryType,
    activity_note: activityNote
  }),
  getSummary: (params = {}) => axios.get(`${API_URL}/time-tracking/summary`, { params }),
  getUserSummary: (userId, params = {}) => axios.get(`${API_URL}/time-tracking/summary/user/${userId}`, { params }),
};

// SOPs API
export const sopsApi = {
  getAll: () => axios.get(`${API_URL}/sops`),
  create: (data) => axios.post(`${API_URL}/sops`, data),
  toggle: (id) => axios.put(`${API_URL}/sops/${id}/toggle`),
  delete: (id) => axios.delete(`${API_URL}/sops/${id}`),
};

// Notifications API - cached
export const notificationsApi = {
  getAll: (unreadOnly = false) => {
    const cacheKey = `notifications:${unreadOnly}`;
    const cached = getCached(cacheKey);
    if (cached) return Promise.resolve(cached);
    return axios.get(`${API_URL}/notifications`, { params: { unread_only: unreadOnly } }).then(res => {
      setCached(cacheKey, res);
      return res;
    });
  },
  markRead: (id) => {
    clearCache('notifications');
    return axios.put(`${API_URL}/notifications/${id}/read`);
  },
  markAllRead: () => {
    clearCache('notifications');
    return axios.put(`${API_URL}/notifications/read-all`);
  },
  getUnreadCount: () => axios.get(`${API_URL}/notifications/count`),
};

// Dashboard API - cached with longer TTL for heavy endpoints
export const dashboardApi = {
  getStats: (params = {}) => {
    const cacheKey = `dashboard:stats:${JSON.stringify(params)}`;
    const cached = getCached(cacheKey, 60000);
    if (cached) return Promise.resolve(cached);
    return axios.get(`${API_URL}/dashboard/stats`, { params }).then(res => {
      setCached(cacheKey, res, 60000);
      return res;
    });
  },
  getPerformance: (params = {}) => {
    const cacheKey = `dashboard:perf:${JSON.stringify(params)}`;
    const cached = getCached(cacheKey, 60000);
    if (cached) return Promise.resolve(cached);
    return axios.get(`${API_URL}/dashboard/performance`, { params }).then(res => {
      setCached(cacheKey, res, 60000);
      return res;
    });
  },
  getRecruitmentFunnel: (params = {}) => {
    const cacheKey = `dashboard:funnel:${JSON.stringify(params)}`;
    const cached = getCached(cacheKey, 60000);
    if (cached) return Promise.resolve(cached);
    return axios.get(`${API_URL}/dashboard/recruitment-funnel`, { params }).then(res => {
      setCached(cacheKey, res, 60000);
      return res;
    });
  },
   getRecruiterPerformance: (params = {}) => {
    const cacheKey = `dashboard:recruiter:${JSON.stringify(params)}`;
    const cached = getCached(cacheKey, 60000);
    if (cached) return Promise.resolve(cached);
    return axios.get(`${API_URL}/dashboard/recruiter-performance`, { params }).then(res => {
      setCached(cacheKey, res, 60000);
      return res;
    });
  },
  getClientPerformance: (params = {}) => {
    const cacheKey = `dashboard:client:${JSON.stringify(params)}`;
    const cached = getCached(cacheKey, 60000);
    if (cached) return Promise.resolve(cached);
    return axios.get(`${API_URL}/dashboard/client-performance`, { params }).then(res => {
      setCached(cacheKey, res, 60000);
      return res;
    });
  },
  exportExcel: (params = {}) => axios.get(`${API_URL}/dashboard/export/excel`, { params, responseType: 'blob' }),
  exportPdf: (params = {}) => axios.get(`${API_URL}/dashboard/export/pdf`, { params, responseType: 'blob' }),
};

// Export API
export const exportApi = {
  dbDump: () => axios.get(`${API_URL}/export/db-dump`, {
    responseType: 'blob',
    timeout: 300000,
  }),
};

// Permission Presets API
export const permissionPresetsApi = {
  getAll: () => axios.get(`${API_URL}/permission-presets`),
  create: (data) => axios.post(`${API_URL}/permission-presets`, data),
  update: (id, data) => axios.put(`${API_URL}/permission-presets/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/permission-presets/${id}`),
};

// Clients API (preset client list)
export const clientsApi = {
  getAll: () => axios.get(`${API_URL}/clients`),
  create: (name) => axios.post(`${API_URL}/clients`, { name }),
  delete: (id) => axios.delete(`${API_URL}/clients/${id}`),
};

// Integrations API
export const integrationsApi = {
  launchBriefDesk: () => axios.get(`${API_URL}/integrations/briefdesk/launch`),
};
